/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { UiIcon } from '../common/icons/ui-icon'
import { concatCssClasses } from '../../utils/concat-css-classes'
import { countNotesInFolder, type NoteTreeFolder } from './build-note-tree'
import { NoteTreeNoteItem } from './note-tree-note-item'
import type { ContextMenuPosition } from './note-tree-context-menu'
import type { NoteTreeEntryInterface } from '@hedgedoc/commons'
import { useOpenNewPageModal } from '../common/page-settings-modal/new-page-modal-context'
import styles from './note-tree-sidebar.module.scss'
import React, { useCallback, useMemo } from 'react'
import {
  ChevronDown as IconChevronDown,
  ChevronRight as IconChevronRight,
  Folder as IconFolder,
  Folder2Open as IconFolderOpen,
  Plus as IconPlus
} from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'

export interface NoteTreeFolderItemProps {
  folder: NoteTreeFolder
  depth: number
  isExpanded: (path: string) => boolean
  onToggle: (path: string) => void
  currentAlias: string | undefined
  onNoteContextMenu: (note: NoteTreeEntryInterface, position: ContextMenuPosition) => void
  /** Whether a "new note in this folder" button is shown */
  allowNewNote?: boolean
}

/**
 * Renders a collapsible folder of the navigation tree with its sub folders and notes.
 */
export const NoteTreeFolderItem: React.FC<NoteTreeFolderItemProps> = ({
  folder,
  depth,
  isExpanded,
  onToggle,
  currentAlias,
  onNoteContextMenu,
  allowNewNote = true
}) => {
  const { t } = useTranslation()
  const expanded = isExpanded(folder.path)
  const noteCount = useMemo(() => countNotesInFolder(folder), [folder])
  const onClick = useCallback(() => onToggle(folder.path), [folder.path, onToggle])
  const openNewPageModal = useOpenNewPageModal()
  const onNewNoteClick = useCallback(() => openNewPageModal({ path: folder.path }), [folder.path, openNewPageModal])

  return (
    <li>
      <div className={concatCssClasses(styles.row, styles.folder)} style={{ paddingLeft: `${depth * 12 + 4}px` }}>
        <button className={styles['row-button']} onClick={onClick} aria-expanded={expanded} title={folder.path}>
          <UiIcon icon={expanded ? IconChevronDown : IconChevronRight} className={styles.chevron} />
          <UiIcon icon={expanded ? IconFolderOpen : IconFolder} className={styles['row-icon']} />
          <span className={styles.label}>{folder.name}</span>
          <span className={styles.count}>{noteCount}</span>
        </button>
        {allowNewNote && (
          <button
            onClick={onNewNoteClick}
            className={styles['new-note']}
            title={t('noteTree.newNoteInFolder', { folder: folder.path })}>
            <UiIcon icon={IconPlus} />
          </button>
        )}
      </div>
      {expanded && (
        <ul>
          {folder.folders.map((subFolder) => (
            <NoteTreeFolderItem
              key={subFolder.path}
              folder={subFolder}
              depth={depth + 1}
              isExpanded={isExpanded}
              onToggle={onToggle}
              currentAlias={currentAlias}
              onNoteContextMenu={onNoteContextMenu}
              allowNewNote={allowNewNote}
            />
          ))}
          {folder.notes.map((note) => (
            <NoteTreeNoteItem
              key={note.primaryAlias}
              note={note}
              depth={depth + 1}
              active={note.primaryAlias === currentAlias}
              onContextMenu={onNoteContextMenu}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
